import { Heightfield } from '../terrain/heightfield'
import { Noise2D, type Rng } from '../core/rng'
import { RAD, clamp01, smoothstep } from '../core/math'

/** Tree density just below the treeline, and on the lowest ground. */
const TREELINE_EDGE = 0.85
const VALLEY_THICK = 3.4

/**
 * Trees and rocks, in a uniform-grid spatial hash.
 * Spec §7: "heightmap sampling for the surface, spatial hash for trees and
 * rock. No mesh collision."
 */

export interface Obstacle {
  x: number
  z: number
  y: number
  /** Collision radius, metres. */
  radius: number
  /** Visual height, metres. */
  height: number
  kind: 'tree' | 'rock'
  /**
   * How much hitting this hurts, roughly 0.3 (a buried rock you clatter over)
   * to 1.2 (a mature tree). Scales both the crash threshold and how much speed
   * survives the contact — clipping a knee-high rock at 30 m/s should cost you
   * a moment of composure, not the run.
   */
  severity: number
  /** Per-instance visual variation, 0..1. */
  variant: number
}

const CELL = 12

export class ObstacleField {
  readonly items: Obstacle[] = []
  private readonly buckets = new Map<number, number[]>()

  private key(cx: number, cz: number): number {
    // Both coords fit comfortably in 16 bits at this cell size.
    return ((cx + 4096) << 16) | (cz + 4096)
  }

  add(o: Obstacle): void {
    const idx = this.items.length
    this.items.push(o)
    const cx = Math.floor(o.x / CELL)
    const cz = Math.floor(o.z / CELL)
    const k = this.key(cx, cz)
    let bucket = this.buckets.get(k)
    if (!bucket) {
      bucket = []
      this.buckets.set(k, bucket)
    }
    bucket.push(idx)
  }

  /**
   * First obstacle overlapping a disc at (x, z). Returns null if clear.
   * Checks the 3x3 neighbourhood, which is correct as long as
   * radius + queryRadius <= CELL.
   */
  query(x: number, z: number, radius: number): Obstacle | null {
    const cx = Math.floor(x / CELL)
    const cz = Math.floor(z / CELL)
    for (let dz = -1; dz <= 1; dz++) {
      for (let dx = -1; dx <= 1; dx++) {
        const bucket = this.buckets.get(this.key(cx + dx, cz + dz))
        if (!bucket) continue
        for (let i = 0; i < bucket.length; i++) {
          const o = this.items[bucket[i]]
          const ddx = o.x - x
          const ddz = o.z - z
          const r = o.radius + radius
          if (ddx * ddx + ddz * ddz < r * r) return o
        }
      }
    }
    return null
  }
}

/**
 * Scatter obstacles onto the face.
 *
 * Placement rules, all deliberate:
 *  - trees only on the lower flanks and apron, where a real face has them, and
 *    never in the fall line of the main sectors
 *  - rocks on and below the cliff band, marking the no-fall zone visually
 *  - nothing steeper than ~48°, nothing on the start ledge
 */
export interface ScatterOptions {
  /**
   * Fraction of the face's vertical, measured up from the base, below which
   * trees grow. Expressed as a fraction rather than an absolute elevation
   * because generated faces put their base at whatever height they like — a
   * hard-coded elevation once grew forest up into 45° couloirs.
   */
  treeline: number
  density: number
  /** Downhill distance past which the runout is kept open. */
  openRunoutZ: number
  /**
   * How far inside the racing corridor a point is: 1 on the floor, 0 clear of
   * it. Trees are suppressed by this.
   *
   * Distance from the slope's *centre* used to stand in for this, which stops
   * being true the moment the floor meanders — a corridor swinging out to +40m
   * would grow trees down the middle of itself.
   */
  corridorFloor?: (x: number, z: number) => number
  /** The corridor band at a z: exactly what the audit gates gap-width on. */
  corridorBand?: (z: number) => { centre: number; half: number }
}

export function scatterObstacles(
  hf: Heightfield,
  rng: Rng,
  opts: ScatterOptions,
): ObstacleField {
  const field = new ObstacleField()
  const noise = new Noise2D(rng)

  const { min: baseY, max: topY } = hf.elevationRange()
  const treelineY = baseY + (topY - baseY) * opts.treeline
  const bandY = (topY - baseY) * 0.12

  const halfWidth = hf.width / 2

  // --- Trees: lower flanks and apron edges --------------------------------
  // Not clamped to 1: a corridor slope has far less ground outside the racing
  // line than an open face did, so it takes a higher density to put the same
  // amount of forest on the shoulders.
  //
  // Every try draws exactly four values, accepted or not. With draws inside
  // the accept branch, any change to a density rule shifts the RNG stream for
  // everything after it — a fix to runout rocks re-rolled every obstacle on
  // the mountain, moved par by seconds, and turned each tweak into a fresh
  // audit. Fixed draws make the layout a pure function of try index and the
  // density field, so a local rule change stays local.
  // The forest thickens downhill. Deliberately thinner than before just under
  // the treeline and considerably thicker low down, so the change is a
  // gradient rather than a uniform lift — the point is to be able to feel
  // where on the mountain you are, not to have more trees everywhere.
  //
  // The runout stays open regardless: the term further down clears the last
  // ninety metres, which is where the rider is slowest and least able to
  // steer, and where a thicket would be a wall rather than a hazard.
    // Tries are the lever that actually adds trees. Low on the mountain the
    // accept gate is saturated — density times 0.95 is past 1, every try
    // passes — so raising the density constants adds nothing there; the count
    // is limited by how many attempts land and the 2.6m spacing. 21000 from
    // 14000 is what "more trees" means in this scatter.
    const TREE_TRIES = Math.round(30000 * Math.max(0, opts.density))
  for (let i = 0; i < TREE_TRIES; i++) {
    const x = hf.minX + rng() * hf.width
    const z = hf.minZ + rng() * hf.length
    const accept = rng()
    const variant = rng()

    const y = hf.sampleHeight(x, z)
    // Above the treeline, nothing; below it, thickening the whole way down.
    //
    // This used to saturate at 1 the moment it cleared the treeline and stay
    // there, which put the same forest on the shoulder at 1800m as at 1200m.
    // Real treelines do not work like that: the band just under one is thin,
    // wind-blasted krummholz, and the stand gets taller and closer together
    // with every hundred metres of descent as shelter, soil and season length
    // improve. Skiing down through that gradient is most of what tells you how
    // far down the mountain you are.
    const aboveLine = 1 - smoothstep(treelineY - bandY, treelineY + bandY, y)
    // Depth below the treeline, as a fraction of the drop from it to the
    // lowest ground on the slope. Elevation rather than distance along the
    // course, because a treeline is an altitude effect — this then works the
    // same on a 470m drainage and a 780m couloir without tuning either.
    const belowLine = clamp01((treelineY - y) / Math.max(1, treelineY - baseY))
    const thicken = TREELINE_EDGE + (VALLEY_THICK - TREELINE_EDGE) * belowLine

    // Keep the racing line genuinely open. Trees are flanking hazards that
    // punish drifting wide, not slalom gates in the fall line.
    //
    // How far outside the corridor this point is, and the small fixed rate at
    // which trees leak into it. The 3% leak that put a handful of trees in the
    // racing line on a 130m floor puts a thicket in a 32m choke, so trees
    // belong on the shoulders; what you dodge in the corridor is the erratics.
    const shoulder = opts.corridorFloor
      ? clamp01(1 - opts.corridorFloor(x, z))
      : clamp01(smoothstep(halfWidth * 0.2, halfWidth * 0.62, Math.abs(x)))
    // Scaled down by exactly the factor the tries went up (14/21), because
    // the leak is a rate *per try*: corridor trees ~ tries x leak, and holding
    // that product constant is what keeps "more forest" from also meaning
    // "more trees standing in the racing line". Last time the corridor count
    // crept up, the par solver's own line hit one and the audit failed.
    const leak = opts.corridorFloor ? 0.03 : 0.05

    // The descent ramp thickens the *shoulders* and leaves the fall line
    // alone. Applied to the whole term instead, as it was at first, it scaled
    // the corridor leak along with everything else — more than doubling the
    // trees standing in the racing line low down, which is exactly where the
    // rider is fastest and least able to avoid one. The par solver's own line
    // hit a tree and the audit called it, which is the check earning its keep:
    // the forest looked right in every screenshot.
    // Linear shoulder again, deliberately. It was squared while par existed,
    // to keep trees off the wall the beam search banked its fast line on —
    // the solver crashing was an audit failure. Par is gone and the game is
    // the trees now: they crowd the floor edge, the corridor carries a real
    // scatter to thread, and the only line that matters is the one the player
    // finds. The bot proves one exists.
    let density = aboveLine * (leak + shoulder * thicken)
    // Open the runout: tree density otherwise peaks at the lowest elevation,
    // exactly where the skier is slowest and least able to steer.
    density *= 1 - smoothstep(opts.openRunoutZ, opts.openRunoutZ + 90, z)
    // Clumping, so they read as a forest and not as scatter.
    density *= clamp01(noise.fbm(x * 0.013, z * 0.012, 3) * 1.5 + 0.55)
    // Trees hold on much steeper ground than this used to allow — a treed
    // gully wall at 48° is an ordinary sight, and cutting them off at 40° left
    // the corridor walls bare exactly where the forest should be densest.
    density *= 1 - smoothstep(48, 56, hf.slopeAngleAt(x, z) * RAD)
    density *= 1 - clamp01(hf.sampleRock(x, z) * 2)

    if (accept > density * 0.95) continue
    if (field.query(x, z, 2.6)) continue
    // In and around the racing line, trees stand alone. On the shoulders the 2.6m
    // spacing lets stands pack into real walls, which is what a forest is —
    // but in a corridor floor that pinches to twenty-six metres, two trees
    // that close are not two obstacles, they are one wall, and the audit bot
    // proved a choke can close completely. Singles you thread; walls you hit.
    if (opts.corridorFloor && opts.corridorFloor(x, z) > 0.3 && field.query(x, z, 6.0)) continue

    field.add({
      x, z, y,
      radius: 1.05 + variant * 0.7,
      height: 5.5 + variant * 7.5,
      kind: 'tree',
      severity: 0.85 + variant * 0.35,
      variant,
    })
  }

  // --- Rocks: the cliff bands, the steeps, and loose ground ---------------
  // Same fixed-draw rule as trees: four draws per try, unconditionally.
  const ROCK_TRIES = 4600
  for (let i = 0; i < ROCK_TRIES; i++) {
    const x = hf.minX + rng() * hf.width
    const z = hf.minZ + rng() * hf.length
    const accept = rng()
    const variant = rng()
    const y = hf.sampleHeight(x, z)
    const slope = hf.slopeAngleAt(x, z) * RAD

    // Follow the rock mask the terrain already computed. Rocks marking a cliff
    // band are how a no-fall zone stays visible from inspection (spec §5.4).
    let density = hf.sampleRock(x, z) * 1.4
    density += 0.35 * clamp01(hf.sampleExposure(x, z) * 1.2)
    // Erratics: the odd boulder sitting out on open ground, clumped so they
    // read as moraine rather than sprinkle. These are the ones you actually
    // have to steer around, so they stay small and mostly survivable.
    const loose = clamp01(noise.fbm(x * 0.017 + 61, z * 0.015 - 40, 3) * 1.9 - 0.55)
    const erratic = loose * (1 - smoothstep(40, 52, slope)) * 0.42
    density += erratic
    // Steep gate lowered from 52-60: the new steering model makes riding high
    // on the 45-50° wall banks the fast line, and a boulder parked on a bank
    // at that pitch is a wall you meet at speed with nowhere to go. The walls
    // already carry fluting and strata; they do not need rocks to read.
    density *= 1 - smoothstep(46, 54, slope)
    density *= smoothstep(0.03, 0.09, z / hf.length)
    // Open the runout, same rule as trees. Rocks skipped it, and the audit's
    // par line duly found one parked eight metres before the finish — where
    // the skier is fastest, most committed, and least able to react. The
    // solver chose to crash through it rather than brake; so would a player.
    density *= 1 - smoothstep(opts.openRunoutZ, opts.openRunoutZ + 90, z)

    if (accept > density * 0.4) continue
    if (field.query(x, z, 2.2)) continue

    // An erratic out in the open is a boulder you clip, not a slab of the
    // cliff band: keep it low and cheap to hit.
    const open = erratic > density * 0.5
    field.add({
      x, z, y,
      radius: open ? 0.7 + variant * 1.0 : 0.9 + variant * 1.6,
      height: open ? 0.6 + variant * 1.3 : 0.8 + variant * 2.4,
      kind: 'rock',
      severity: open ? 0.25 + variant * 0.3 : 0.3 + variant * 0.55,
      variant,
    })
  }

  // --- The line the audit gates on, enforced rather than hoped for --------
  // check-slopes requires every 10m band of the corridor floor to keep a gap
  // wide enough to ski. That used to be left to the dice: each version bump
  // reseeds the scatter, and v10's roll closed two bands to under six metres
  // — obstacles legally placed, spacing rules all satisfied, line still shut.
  // Rolling versions until the audit passes is not a fix. Measure each band
  // the same way the audit does and delete the single obstacle pinching it,
  // repeating until the band opens: deterministic, a handful of trees per
  // mountain, and the gate becomes something the scatter guarantees.
  if (opts.corridorFloor && opts.corridorBand) {
    const MARGIN = 0.9
    const GAP = 7          // the audit gates at 6; enforce with headroom
    const doomed = new Set<Obstacle>()
    // Every metre, not every five. The audit's bands sit on ITS grid, and a
    // window centred even two metres off sees a different obstacle subset —
    // the z=128 pinch was measured healthy from 125 and from 130, because
    // each offset window missed one flank of the cluster the aligned window
    // catches whole. Stepping at 1m makes every possible alignment a subset
    // of what enforcement checked. Costs a few million comparisons, once,
    // at build.
    for (let z = hf.minZ + 20; z < hf.minZ + hf.length - 10; z += 1) {
      for (let attempt = 0; attempt < 6; attempt++) {
        // The exact band the audit measures — centre and half-width from the
        // corridor geometry. The first version derived the extent from
        // floorAt > 0.5, which spans ~20m wider: it saw open flank outside
        // the audited band, called every band healthy, and removed nothing
        // while the audit kept failing on the same 4.7m pinch.
        if (!opts.corridorBand) break
        const band = opts.corridorBand(z)
        const lo = band.centre - band.half
        const hi = band.centre + band.half
        if (hi - lo < GAP) break // floor narrower than the gap; geometry's problem
        const blockers = field.items.filter((o) =>
          !doomed.has(o) && Math.abs(o.z - z) <= 5 &&
          o.x + o.radius + MARGIN > lo && o.x - o.radius - MARGIN < hi)
        const edges = blockers
          .map((o) => [Math.max(lo, o.x - o.radius - MARGIN), Math.min(hi, o.x + o.radius + MARGIN)] as const)
          .sort((a, b) => a[0] - b[0])
        let cursor = lo
        let widest = 0
        for (const [a, b] of edges) {
          widest = Math.max(widest, a - cursor)
          cursor = Math.max(cursor, b)
        }
        widest = Math.max(widest, hi - cursor)
        if (widest >= GAP) break
        // Remove whichever blocker covers the most floor: the pinch-maker.
        let best: Obstacle | null = null
        let bestCover = -1
        for (const o of blockers) {
          const cover = Math.min(hi, o.x + o.radius + MARGIN) - Math.max(lo, o.x - o.radius - MARGIN)
          if (cover > bestCover) { bestCover = cover; best = o }
        }
        if (!best) break
        doomed.add(best)
      }
    }
    if (doomed.size > 0) {
      const survivors = field.items.filter((o) => !doomed.has(o))
      const rebuilt = new ObstacleField()
      for (const o of survivors) rebuilt.add(o)
      return rebuilt
    }
  }

  return field
}
